import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { resolveCarBookingPayment } from '../carBookingPayment';
import { formatMoney } from '../money';
import type { TravelBooking } from '../queries';

interface Props {
  booking: TravelBooking;
  isAgent: boolean;
}

/**
 * Desglose de lectura de Cars. El total, la base y cada cargo se formatean tal
 * como llegaron; este componente nunca suma filas ni decide qué cobra quién.
 */
export function CarBookingPaymentBreakdown({ booking, isAgent }: Props) {
  const { t } = useTranslation();
  const view = resolveCarBookingPayment(booking);
  if (!view) return null;
  const hasAgentAmounts = isAgent && (view.providerTotal || view.serviceBase);
  if (!hasAgentAmounts && !view.paymentType && view.pickupCharges.length === 0) {
    return null;
  }

  return (
    <Box mt={1}>
      <Typography variant="subtitle2">{t('travelServices.detail.carPaymentBreakdown')}</Typography>
      {isAgent && view.providerTotal && (
        <Typography variant="body2" color="text.secondary">
          {t('travelServices.detail.providerTotal')}: {formatMoney(view.providerTotal)}
        </Typography>
      )}
      {isAgent && view.serviceBase && (
        <Typography variant="body2" color="text.secondary">
          {t('travelServices.detail.serviceBase')}: {formatMoney(view.serviceBase)}
        </Typography>
      )}
      {view.paymentType && (
        <Typography variant="body2" color="text.secondary">
          {t(`travelServices.detail.payment.${view.paymentType}`)}
        </Typography>
      )}
      {view.pickupCharges.map((charge, index) => (
        <Typography key={`${charge.name ?? 'charge'}-${index}`} variant="body2" color="text.secondary">
          {t('travelServices.detail.pickupCharge', {
            name: charge.name ?? t('travelServices.detail.unnamedCharge'),
            amount: formatMoney(charge.amount),
          })}
        </Typography>
      ))}
    </Box>
  );
}
